import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  RateReview as RateReviewIcon,
  AutoAwesome as AiIcon,
  CheckCircle as CheckIcon,
  Cancel as WrongIcon,
  Lightbulb as HintIcon,
  HelpOutline as HelpIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api, { getMockExamApiPrefix, getMockExamPortalHomePath, getMockExamResultPath } from '../../../api';
import useExamMode from '../../../hooks/useExamMode';
import MarksBreakdownTable from '../components/MarksBreakdownTable';
import HtmlWithLatex from '../../QuestionBank/components/HtmlWithLatex';
import { resolveLeafVerdict } from '../utils/leafMarkVerdict';
import { toMockLeafRow } from '../utils/leafRow';
import InteractiveAnswerBlock from '../components/InteractiveAnswerBlock';
import { fmtMarks, truncateHtml, formatAnswerLabel, plainLinesToHtml } from '../utils/breakdownFormat';

const COLORS = ['#0088FE', '#FF8042', '#FFBB28', '#00C49F'];

const PERFORMANCE_LEVEL_CONFIG = {
  excellent: { label: 'Excellent', bgcolor: '#dcfce7', color: '#166534' },
  good: { label: 'Good', bgcolor: '#dbeafe', color: '#1e40af' },
  average: { label: 'Average', bgcolor: '#fef9c3', color: '#854d0e' },
  needs_improvement: { label: 'Needs Improvement', bgcolor: '#fee2e2', color: '#991b1b' },
  unknown: { label: 'Unknown', bgcolor: '#f3f4f6', color: '#374151' },
};

// ---- AI Insights Tab Content ----
const AiInsightsTab = ({ aiFeedbackStatus, aiOverall, aiScore, aiPercentage, totalMarks, ruleScore }) => {
  if (aiFeedbackStatus === 'pending' || aiFeedbackStatus === 'processing') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
        <CircularProgress size={40} sx={{ color: '#7c3aed' }} />
        <Typography variant="body1" sx={{ color: '#7c3aed', fontWeight: 500 }}>
          AI is analysing your answers…
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This usually takes 5–15 seconds. The page will update automatically.
        </Typography>
      </Box>
    );
  }

  if (aiFeedbackStatus === 'failed' || !aiOverall) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        AI feedback is not available for this attempt. This may be due to a temporary issue.
      </Alert>
    );
  }

  const perfConfig = PERFORMANCE_LEVEL_CONFIG[aiOverall.performance_level] ?? PERFORMANCE_LEVEL_CONFIG.unknown;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Score comparison card */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Card variant="outlined" sx={{ borderColor: '#e0d7ff', bgcolor: '#f3f0ff' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AiIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#7c3aed' }}>
                AI-Adjusted Score
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#7c3aed', mb: 0.5 }}>
              {Number(aiScore ?? 0).toFixed(1)}
              <Typography component="span" variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                {' '}/ {Number(totalMarks ?? 0).toFixed(1)}
              </Typography>
            </Typography>
            <Typography variant="body2" sx={{ color: '#7c3aed', fontWeight: 600 }}>
              {Number(aiPercentage ?? 0).toFixed(1)}%
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Based on semantic answer matching
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TrendingUpIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Performance Level
              </Typography>
            </Box>
            <Chip
              label={perfConfig.label}
              sx={{
                bgcolor: perfConfig.bgcolor,
                color: perfConfig.color,
                fontWeight: 700,
                fontSize: '1rem',
                height: 36,
                px: 1,
                mb: 1,
              }}
            />
            {ruleScore != null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Auto Score: {Number(ruleScore).toFixed(1)} / {Number(totalMarks ?? 0).toFixed(1)}
                {Number(ruleScore) === 0 && (
                  <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.disabled' }}>
                    (drawings/essays scored by AI only)
                  </Typography>
                )}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Strengths & Weaknesses */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {aiOverall.strengths?.length > 0 && (
          <Card variant="outlined" sx={{ borderColor: '#bbf7d0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#166534' }}>
                  Strengths
                </Typography>
              </Box>
              {aiOverall.strengths.map((s, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#16a34a', mt: 0.7, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {s}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {aiOverall.weaknesses?.length > 0 && (
          <Card variant="outlined" sx={{ borderColor: '#fecaca' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <WrongIcon sx={{ color: '#dc2626', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#991b1b' }}>
                  Areas to Improve
                </Typography>
              </Box>
              {aiOverall.weaknesses.map((w, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#dc2626', mt: 0.7, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {w}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Study Recommendations */}
      {aiOverall.recommendations?.length > 0 && (
        <Card variant="outlined" sx={{ borderColor: '#bfdbfe' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <HintIcon sx={{ color: '#2563eb', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e40af' }}>
                Study Recommendations
              </Typography>
            </Box>
            {aiOverall.recommendations.map((r, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: '#dbeafe',
                    color: '#1e40af',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    flexShrink: 0,
                    mt: 0.1,
                  }}
                >
                  {i + 1}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {r}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

const StudentMockExamReport = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { isTestingMode } = useExamMode();
  const isTestMode = isTestingMode('mock_exams');
  const [tab, setTab] = useState('score');
  const [summary, setSummary] = useState(null);
  const [attemptMeta, setAttemptMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subjectData, setSubjectData] = useState(null);
  const [subjectLoading, setSubjectLoading] = useState(false);
  const [topicData, setTopicData] = useState(null);
  const [topicLoading, setTopicLoading] = useState(false);
  const [error, setError] = useState(null);

  // AI feedback state
  const [aiFeedbackStatus, setAiFeedbackStatus] = useState('pending');
  const [aiOverall, setAiOverall] = useState(null);
  const [aiScore, setAiScore] = useState(null);
  const [aiPercentage, setAiPercentage] = useState(null);
  const [aiQuestionFeedback, setAiQuestionFeedback] = useState({});
  const [breakdownPage, setBreakdownPage] = useState(0);
  const BREAKDOWN_PER_PAGE = 15;
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);
  const MAX_POLLS = 20;

  useEffect(() => {
    const handleExamModeChanged = () => {
      window.location.reload();
    };
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  // Review data for question-level breakdown (correct / partially correct / incorrect)
  const [reviewQuestions, setReviewQuestions] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!attemptId) return;
    setLoading(true);
    setError(null);
    try {
      const px = getMockExamApiPrefix();
      const [attemptRes, analyticsRes] = await Promise.all([
        api.get(`/${px}/mock-exam/attempt/${attemptId}`),
        api.get(`/${px}/mock-exam/attempt/${attemptId}/analytics`),
      ]);
      setAttemptMeta(attemptRes.data?.data ?? attemptRes.data);
      // The breakdown is built from the server's own per-row grading, so it must not wait on the AI —
      // the page used to render nothing at all until ai_feedback_status flipped to "completed".
      try {
        const reviewRes = await api.get(`/${px}/mock-exam/attempt/${attemptId}/review`);
        const reviewData = reviewRes.data?.data ?? reviewRes.data;
        setReviewQuestions(reviewData?.questions ?? null);
      } catch {
        // Non-critical
      }
      const analyticsData = analyticsRes.data?.data ?? analyticsRes.data;
      setSummary(analyticsData);
      // Seed AI state from analytics response
      if (analyticsData?.ai_feedback_status) {
        setAiFeedbackStatus(analyticsData.ai_feedback_status);
      }
      if (analyticsData?.ai_score != null) {
        setAiScore(analyticsData.ai_score);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load report');
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [attemptId]);

  const fetchAiFeedback = useCallback(async () => {
    if (!attemptId) return;
    try {
      const res = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/ai-feedback`);
      const payload = res.data?.data ?? res.data;
      const status = payload?.status ?? 'pending';
      setAiFeedbackStatus(status);
      if (status === 'completed') {
        setAiOverall(payload?.overall ?? null);
        const newAiScore = payload?.ai_score ?? null;
        setAiScore(newAiScore);
        setAiPercentage(payload?.ai_percentage ?? null);
        setAiQuestionFeedback(payload?.question_feedback ?? {});
        clearInterval(pollRef.current);
        // Re-fetch analytics summary so the Score tab reflects the AI-adjusted final_score
        try {
          const analyticsRes = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/analytics`);
          const analyticsData = analyticsRes.data?.data ?? analyticsRes.data;
          setSummary(analyticsData);
        } catch {
          // Non-critical: keep existing summary
        }
        // Fetch review so we can show correct / partially correct / incorrect breakdown
        try {
          const reviewRes = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/review`);
          const reviewData = reviewRes.data?.data ?? reviewRes.data;
          setReviewQuestions(reviewData?.questions ?? null);
        } catch {
          // Non-critical
        }
      } else if (status === 'failed') {
        clearInterval(pollRef.current);
      }
    } catch {
      // Silently ignore polling errors
    }
  }, [attemptId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Start polling for AI feedback
  useEffect(() => {
    if (!attemptId) return;
    fetchAiFeedback();
    pollRef.current = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current >= MAX_POLLS) {
        clearInterval(pollRef.current);
        return;
      }
      fetchAiFeedback();
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [attemptId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (aiFeedbackStatus === 'completed' || aiFeedbackStatus === 'failed') {
      clearInterval(pollRef.current);
    }
  }, [aiFeedbackStatus]);

  const fetchSubject = useCallback(async () => {
    if (!attemptId) return;
    setSubjectLoading(true);
    try {
      const res = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/analytics/subject`);
      setSubjectData(res.data?.data ?? res.data);
    } catch (err) {
      toast.error('Failed to load subject analytics');
      setSubjectData([]);
    } finally {
      setSubjectLoading(false);
    }
  }, [attemptId]);

  const fetchTopic = useCallback(async () => {
    if (!attemptId) return;
    setTopicLoading(true);
    try {
      const res = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/analytics/topic`);
      setTopicData(res.data?.data ?? res.data);
    } catch (err) {
      toast.error('Failed to load topic analytics');
      setTopicData([]);
    } finally {
      setTopicLoading(false);
    }
  }, [attemptId]);

  useEffect(() => {
    if (tab === 'subject' && subjectData === null && !subjectLoading) fetchSubject();
  }, [tab, subjectData, subjectLoading, fetchSubject]);

  useEffect(() => {
    if (tab === 'topic' && topicData === null && !topicLoading) fetchTopic();
  }, [tab, topicData, topicLoading, fetchTopic]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error || !summary) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Card sx={{ p: 4, textAlign: 'center', maxWidth: 440 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <TrendingUpIcon sx={{ fontSize: 32, color: '#374151' }} />
          </Box>
          <Typography variant="h6" gutterBottom color="text.secondary">
            Report could not be loaded
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {error || 'This report is not available. The attempt may have been removed or you may not have access.'}
          </Typography>
          <Button variant="contained" onClick={() => navigate(getMockExamPortalHomePath())}>
            Back to Mock Exams
          </Button>
        </Card>
      </Box>
    );
  }

  // Use AI score for display if available
  const displayScore = aiScore != null ? aiScore : (summary.final_score ?? summary.scored_marks ?? 0);
  const displayPercentage = aiScore != null && summary.total_marks > 0
    ? Math.round((aiScore / summary.total_marks) * 1000) / 10
    : (summary.percentage ?? 0);

  // One verdict engine with the Review page and the past-paper Result: resolveLeafVerdict reads the
  // server's own per-row grading (pooled blanks, answer_variants, partial ratios) and only lets the AI
  // decide free-text rows the rules could not. The inline matcher this replaces ignored the server
  // verdict entirely, had no "written" state, and paid a flat half mark for every partial.
  const verdictCtx = { aiFeedbackMap: aiQuestionFeedback, aiFeedbackStatus };

  // Group the review rows the way the past-paper Result does: one group per top-level question, its
  // sub-questions as parts. A standalone question is a one-part group.
  const reviewGroups = (Array.isArray(reviewQuestions) ? reviewQuestions : []).map((q, i) => {
    const isGrouped = q.type === 'grouped' && Array.isArray(q.sub_questions) && q.sub_questions.length > 0;
    const parts = (isGrouped ? q.sub_questions : [q]).map((item, pi) => ({
      raw: item,
      parent: q,
      leaf: resolveLeafVerdict(toMockLeafRow(item, q), verdictCtx),
      partLetter: isGrouped ? String.fromCharCode(97 + pi) : '',
    }));
    return { mainNum: String(i + 1), questionId: q.id, isGrouped, parts };
  });

  const allParts = reviewGroups.flatMap((g) => g.parts);
  const hasBreakdown = allParts.length > 0;

  const aiBreakdown = hasBreakdown
    ? allParts.reduce(
        (acc, p) => {
          const key = { correct: 'correct', partial: 'partiallyCorrect', unanswered: 'unattended' }[p.leaf.result] ?? 'incorrect';
          acc[key] += 1;
          return acc;
        },
        { correct: 0, partiallyCorrect: 0, incorrect: 0, unattended: 0 }
      )
    : null;

  // When AI breakdown is available, use AI sub-question counts as the authoritative total
  const displayTotalQuestions = aiBreakdown
    ? aiBreakdown.correct + aiBreakdown.partiallyCorrect + aiBreakdown.incorrect + aiBreakdown.unattended
    : (summary.paper_total_questions ?? summary.total_questions ?? 0);

  // Unattended marks from the review rows, so a drawing-only answer isn't counted as unattended.
  const aiUnattendedMarks = hasBreakdown
    ? allParts.reduce((t, p) => t + (p.leaf.result === 'unanswered' ? Number(p.raw.marks ?? 0) : 0), 0)
    : null;

  const marksBreakdownRows = reviewGroups.flatMap((group) =>
    group.parts.map((p, pi) => ({
      key: `${p.raw.id ?? group.questionId}-${pi}`,
      qLabel: pi === 0 ? `Q${group.mainNum}` : '',
      partLabel: group.isGrouped ? p.partLetter.toUpperCase() : '',
      questionText: p.raw.question_text,
      result: p.leaf.result === 'unanswered' ? 'unanswered' : p.leaf.result,
      earned: Number(p.leaf.earned ?? 0),
      max: Number(p.raw.marks ?? 0),
      isMainRow: pi === 0,
    }))
  );

  // The tab total has to agree with the rows under it, so sum the parts and floor at the stored rule
  // score — the same reconciliation StudentPaperResult does.
  const partScored = allParts.reduce((t, p) => t + Number(p.leaf.earned ?? 0), 0);
  const breakdownScore = hasBreakdown ? Math.max(partScored, Number(summary.scored_marks ?? 0)) : displayScore;
  // Header, charts and Marks tab all read the same number, so the score can never disagree with the
  // rows printed under it.
  const shownScore = hasBreakdown ? breakdownScore : displayScore;
  const shownPercentage = summary.total_marks > 0
    ? Math.round((shownScore / summary.total_marks) * 1000) / 10
    : displayPercentage;

  const mockExamId = attemptMeta?.mock_exam_id ?? summary?.mock_exam_id ?? null;

  const barChartData = [
    {
      name: 'Performance',
      totalMarks: summary.total_marks ?? 0,
      marksScored: shownScore,
    },
  ];

  const pieChartData = aiBreakdown
    ? [
        { name: 'Correct', value: aiBreakdown.correct },
        { name: 'Partially correct', value: aiBreakdown.partiallyCorrect },
        { name: 'Incorrect', value: aiBreakdown.incorrect },
        { name: 'Unattended', value: aiBreakdown.unattended },
      ].filter((d) => d.value > 0)
    : [
        { name: 'Correct', value: summary.correct_questions ?? 0 },
        { name: 'Incorrect', value: summary.incorrect_questions ?? 0 },
        { name: 'Unattended', value: summary.unattended_questions ?? 0 },
      ].filter((d) => d.value > 0);

  const subjectRows = Array.isArray(subjectData) ? subjectData : [];
  const topicRows = Array.isArray(topicData) ? topicData : [];

  const topicChartData = topicRows
    .filter((r) => r.name !== 'Grand Total')
    .map((r) => ({
      name: r.name,
      maxMarks: r.max_marks ?? 0,
      correctMarks: (r.correct_marks ?? 0) - (r.incorrect_marks ?? 0),
    }));

  const getQuestionLink = (item) => {
    // Strip sub-question suffix (bank_123_sub_0 → bank_123) to scroll to the parent card
    const parentId = typeof item.questionId === 'string'
      ? item.questionId.replace(/_sub_\d+$/, '')
      : String(item.questionId ?? '');
    if (parentId) {
      return `${getMockExamResultPath(attemptId, 'review')}#question-${parentId}`;
    }
    return null;
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Button size="small" sx={{ mb: 2 }} onClick={() => navigate(getMockExamPortalHomePath())}>
        Back to Mock Exams
      </Button>
      {isTestMode && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}>
          PRACTICE MODE — This is a practice attempt. Results are not recorded.
        </Alert>
      )}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            {summary.exam_title || attemptMeta?.exam_name}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
            Analytics Report for {summary.student_name || attemptMeta?.student_name || 'Student'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {aiScore != null ? (
              <>
                <Tooltip title="AI-adjusted score (semantic matching)">
                  <Chip
                    icon={<AiIcon sx={{ fontSize: 16 }} />}
                    label={`AI Score: ${Number(shownScore).toFixed(1)} / ${Number(summary.total_marks ?? 0).toFixed(1)}`}
                    color="secondary"
                    sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
                <Chip label={`${Number(shownPercentage).toFixed(1)}%`} variant="outlined" color="secondary" />
                <Chip
                  label={`Auto Score: ${Number(aiScore).toFixed(1)} / ${Number(summary.total_marks ?? 0).toFixed(1)}`}
                  variant="outlined"
                  size="small"
                  sx={{ color: 'text.secondary' }}
                />
              </>
            ) : (
              <>
                <Chip
                  label={`Score: ${Number(shownScore).toFixed(1)} / ${Number(summary.total_marks ?? 0).toFixed(1)}`}
                  color="primary"
                />
                <Chip label={`${Number(shownPercentage).toFixed(1)}%`} variant="outlined" />
              </>
            )}
            {(aiFeedbackStatus === 'pending' || aiFeedbackStatus === 'processing') && (
              <Chip
                icon={<CircularProgress size={12} sx={{ color: '#7c3aed' }} />}
                label="AI feedback generating…"
                size="small"
                sx={{ bgcolor: '#f3f0ff', color: '#7c3aed', border: '1px solid #e0d7ff' }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab value="score" label="Score" />
        <Tab value="subject" label="Subject" />
        <Tab value="topic" label="Topic" />
        <Tab value="marks" label="Marks Breakdown" />
        <Tab
          value="ai"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AiIcon sx={{ fontSize: 16 }} />
              AI Insights
              {(aiFeedbackStatus === 'pending' || aiFeedbackStatus === 'processing') && (
                <CircularProgress size={10} sx={{ ml: 0.5, color: '#7c3aed' }} />
              )}
            </Box>
          }
        />
      </Tabs>

      {tab === 'score' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Questions
                </Typography>
                <Typography variant="body2">Total: {displayTotalQuestions}</Typography>
                {aiBreakdown ? (
                  <>
                    <Typography variant="body2" color="success.main">
                      Correct: {aiBreakdown.correct}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#b45309' }}>
                      Partially correct: {aiBreakdown.partiallyCorrect}
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      Incorrect: {aiBreakdown.incorrect}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Unattended: {aiBreakdown.unattended}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" color="success.main">
                      Correct: {summary.correct_questions}
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      Incorrect: {summary.incorrect_questions}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Unattended: {summary.unattended_questions}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Marks
                </Typography>
                <Typography variant="body2">
                  Marking: +{(summary.marks_per_correct_answer ?? 1).toFixed(2)} / -
                  {(summary.negative_marks_penalty ?? 0).toFixed(2)}
                </Typography>
                <Typography variant="body2">Total: {summary.total_marks}</Typography>
                <Typography variant="body2" color="success.main">
                  Scored: {Number(shownScore).toFixed(1)}
                  {aiScore != null && (
                    <Typography component="span" variant="caption" sx={{ color: '#7c3aed', ml: 0.5 }}>
                      (AI)
                    </Typography>
                  )}
                </Typography>
                {aiScore != null && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    Partial credit applied for sub-questions, multi-fill blanks, and table cells.
                  </Typography>
                )}
                <Typography variant="body2" color="error.main">
                  Negative: {summary.negative_marks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unattended: {aiUnattendedMarks != null ? Number(aiUnattendedMarks).toFixed(1) : summary.unattended_marks}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Summary
                </Typography>
                <Typography variant="body2">Time Taken: {summary.time_taken ?? 'N/A'}</Typography>
                <Typography variant="body2">
                  Final Score: {Number(shownScore).toFixed(1)}/{summary.total_marks}
                  {aiScore != null && (
                    <Typography component="span" variant="caption" sx={{ color: '#7c3aed', ml: 0.5 }}>
                      (AI-adjusted)
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Percentage: {Number(shownPercentage).toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Performance Overview
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={barChartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tickLine={false} />
                    <YAxis tickLine={false} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="totalMarks" name="Total Marks" fill="#8884d8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="marksScored" name={aiScore != null ? 'AI Score' : 'Marks Scored'} fill={aiScore != null ? '#7c3aed' : '#82ca9d'} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Question Analysis
                </Typography>
                {pieChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="text.secondary">No question breakdown (all unattended).</Typography>
                )}
              </CardContent>
            </Card>
          </Box>
          {hasBreakdown && (() => {
            const totalPages = Math.ceil(reviewGroups.length / BREAKDOWN_PER_PAGE);
            const pagedGroups = reviewGroups.slice(breakdownPage * BREAKDOWN_PER_PAGE, (breakdownPage + 1) * BREAKDOWN_PER_PAGE);

            return (
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Question Breakdown</Typography>
                    {totalPages > 1 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button size="small" variant="outlined" disabled={breakdownPage === 0}
                          onClick={() => setBreakdownPage((p) => p - 1)} sx={{ minWidth: 0, px: 1.5 }}>&#8249; Prev</Button>
                        <Typography variant="caption" color="text.secondary">
                          {breakdownPage + 1} / {totalPages}
                        </Typography>
                        <Button size="small" variant="outlined" disabled={breakdownPage >= totalPages - 1}
                          onClick={() => setBreakdownPage((p) => p + 1)} sx={{ minWidth: 0, px: 1.5 }}>Next &#8250;</Button>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {pagedGroups.map((group) => {
                      const groupTotalMarks = group.parts.reduce((t, p) => t + Number(p.raw.marks ?? 0), 0);
                      const groupEarned = group.parts.reduce((t, p) => t + Number(p.leaf.earned ?? 0), 0);
                      const results = group.parts.map((p) => p.leaf.result);
                      // The chip summarises the WHOLE question, so it follows the marks the rows below
                      // it show: one wrong part must not paint a question red when its siblings earned
                      // marks, and a question answered as far as the student got is partial, not grey.
                      const answeredResults = results.filter((r) => r !== 'unanswered');
                      const groupResult = answeredResults.length === 0
                        ? 'unanswered'
                        : answeredResults.every((r) => r === 'written')
                          ? 'written'
                          : results.every((r) => r === 'correct') || (groupTotalMarks > 0 && groupEarned >= groupTotalMarks - 1e-9)
                            ? 'correct'
                            : groupEarned > 0
                              ? 'partial'
                              : 'incorrect';
                      const allCorrect = groupResult === 'correct';
                      const anyIncorrect = groupResult === 'incorrect';
                      const summaryChipColor = { correct: 'success', partial: 'warning', incorrect: 'error' }[groupResult] ?? 'default';
                      const summaryChipLabel = { correct: 'Correct', partial: 'Partial', incorrect: 'Incorrect', unanswered: 'Not answered' }[groupResult] ?? 'Written';
                      const previewText = (() => {
                        for (const part of group.parts) {
                          const t = truncateHtml(part.raw.question_text ?? part.parent?.question_text, 90);
                          if (t.trim()) return t;
                        }
                        const hasFigure = group.parts.some((p) => p.raw?.image_url || p.parent?.image_url
                          || (Array.isArray(p.raw?.image_urls) && p.raw.image_urls.length)
                          || (Array.isArray(p.parent?.image_urls) && p.parent.image_urls.length));
                        return hasFigure ? "See figure" : null;
                      })();

                      return (
                        <Accordion key={group.questionId ?? group.mainNum} disableGutters elevation={0}
                          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', overflow: 'hidden', '&:before': { display: 'none' } }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}
                            sx={{ px: 2, minHeight: 52, '& .MuiAccordionSummary-content': { my: 0.75, alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mr: 1 } }}>
                            <Typography sx={{ fontWeight: 700, minWidth: 34, color: 'text.secondary', fontSize: '0.85rem' }}>
                              Q{group.mainNum}
                            </Typography>
                            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', maxHeight: '2.4em',
                              fontSize: '0.875rem', lineHeight: 1.6, color: 'text.primary',
                              '& .katex': { fontSize: '0.85em' },
                              // Only the block wrappers go inline: forcing it onto KaTeX's own spans
                              // collapsed a stacked fraction out of the clipped row (Dulwich 2025 I Q5).
                              '& p, & div': { display: 'inline', m: 0 }, '& .katex-display': { display: 'inline', m: 0 } }}>
                              {previewText
                                ? <HtmlWithLatex html={previewText} />
                                : <Typography component="span" variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>expand to view</Typography>
                              }
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                              {group.isGrouped && (
                                <Chip label={`${group.parts.length} parts`} size="small" variant="outlined"
                                  sx={{ fontSize: '0.68rem', height: 20, display: { xs: 'none', sm: 'inline-flex' } }} />
                              )}
                              <Chip
                                icon={allCorrect ? <CheckIcon sx={{ fontSize: 12 }} /> : anyIncorrect ? <WrongIcon sx={{ fontSize: 12 }} /> : undefined}
                                label={summaryChipLabel} size="small" color={summaryChipColor}
                                sx={{ fontWeight: 700, fontSize: '0.68rem', height: 22 }}
                              />
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap', minWidth: 36, textAlign: 'right' }}>
                                {fmtMarks(groupEarned)}/{fmtMarks(groupTotalMarks)}
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 0, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Box>
                              {group.parts.map((part, pi) => {
                                const { leaf, raw } = part;
                                const aiJudged = leaf.aiDecided;
                                // Several boxes: number both columns so box 2 of "Your Answer" lines up
                                // with box 2 of "Correct Answer" instead of two run-on " | " strings.
                                const perBox = Array.isArray(raw.correct_answer) && raw.correct_answer.length > 1;
                                const boxList = (arr) => (Array.isArray(arr) ? arr : String(arr ?? '').split(/\s*\|\s*/))
                                  .map((v, i) => `<div>${i + 1}) ${formatAnswerLabel(v) || '&#8212;'}</div>`).join('');
                                const correctLabel = Array.isArray(raw.correct_answer)
                                  ? raw.correct_answer.join(' | ')
                                  : raw.correct_answer;
                                const yourHtml = perBox && raw.submitted_answer != null && raw.submitted_answer !== ''
                                  ? boxList(raw.submitted_answer)
                                  : plainLinesToHtml(formatAnswerLabel(raw.submitted_answer));
                                const correctHtml = perBox ? boxList(raw.correct_answer) : plainLinesToHtml(formatAnswerLabel(correctLabel));
                                return (
                                  <Box key={raw.id ?? pi}
                                    sx={{ px: 2, py: 1.5, bgcolor: pi % 2 === 1 ? '#fafafa' : 'transparent',
                                      borderBottom: pi < group.parts.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                                    <Box sx={{ display: 'grid', gap: 1,
                                      gridTemplateColumns: { xs: 'auto 1fr', sm: '36px 1fr 1fr auto auto', md: '36px 1.2fr 0.9fr 0.9fr auto auto' },
                                      alignItems: 'start' }}>
                                      <Box sx={{ pt: 0.25 }}>
                                        {part.partLetter ? (
                                          <Chip label={part.partLetter.toUpperCase()} size="small" color="primary" variant="outlined"
                                            sx={{ fontWeight: 700, fontSize: '0.68rem', height: 22, minWidth: 28 }} />
                                        ) : (
                                          <Typography variant="caption" color="text.disabled">&#8212;</Typography>
                                        )}
                                      </Box>
                                      <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>Question</Typography>
                                        <Typography variant="caption" sx={{ wordBreak: 'break-word', lineHeight: 1.5 }}>
                                          <HtmlWithLatex html={truncateHtml(raw.question_text, 120)} />
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>Your Answer</Typography>
                                        <Box sx={{ fontSize: '0.75rem', wordBreak: 'break-word' }}>
                                          <HtmlWithLatex html={yourHtml || '&#8212;'} />
                                        </Box>
                                        {raw.submitted_drawing && (
                                          <Box component="img" src={raw.submitted_drawing} alt="Submitted drawing"
                                            sx={{ mt: 0.5, maxWidth: 90, maxHeight: 60, borderRadius: 1, border: '1px solid', borderColor: 'divider', display: 'block' }} />
                                        )}
                                      </Box>
                                      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>Correct Answer</Typography>
                                        <Box sx={{ fontSize: '0.75rem', wordBreak: 'break-word' }}>
                                          <HtmlWithLatex html={correctHtml || '&#8212;'} />
                                        </Box>
                                      </Box>
                                      <Box>
                                        {leaf.result === 'unanswered' ? (
                                          <Chip label="Not answered" size="small" color="default" variant="outlined" sx={{ fontWeight: 600 }} />
                                        ) : leaf.result === 'written' ? (
                                          <Chip icon={<HelpIcon />} label="Written" size="small" color="default" sx={{ fontWeight: 600 }} />
                                        ) : leaf.result === 'correct' ? (
                                          <Chip icon={aiJudged ? <AiIcon /> : <CheckIcon />}
                                            label={aiJudged ? 'AI Correct' : leaf.isEquiv ? 'Equiv.' : 'Correct'}
                                            size="small" color="success" sx={{ fontWeight: 700 }} />
                                        ) : leaf.result === 'partial' ? (
                                          <Chip label="Partial" size="small" sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }} />
                                        ) : (
                                          <Chip icon={aiJudged ? <AiIcon /> : <WrongIcon />}
                                            label={aiJudged ? 'AI Incorrect' : 'Incorrect'}
                                            size="small" color="error" sx={{ fontWeight: 700 }} />
                                        )}
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>Marks</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                          {fmtMarks(leaf.earned)} / {fmtMarks(raw.marks)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    {(raw.correct_interactive || raw.answer_data?.interactive) && (
                                      <InteractiveAnswerBlock row={raw} />
                                    )}
                                    <Box sx={{ mt: 0.75 }}>
                                      <Link
                                        component="button"
                                        type="button"
                                        variant="caption"
                                        onClick={() => navigate(getQuestionLink({ questionId: raw.id ?? group.questionId }))}
                                        sx={{ textDecoration: 'underline' }}
                                      >
                                        View in review
                                      </Link>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
                      <Button size="small" variant="outlined" disabled={breakdownPage === 0}
                        onClick={() => setBreakdownPage((p) => p - 1)} sx={{ minWidth: 0, px: 1.5 }}>&#8249; Prev</Button>
                      <Typography variant="caption" color="text.secondary">
                        Page {breakdownPage + 1} of {totalPages} &#183; Q{breakdownPage * BREAKDOWN_PER_PAGE + 1}&#8211;Q{Math.min((breakdownPage + 1) * BREAKDOWN_PER_PAGE, reviewGroups.length)}
                      </Typography>
                      <Button size="small" variant="outlined" disabled={breakdownPage >= totalPages - 1}
                        onClick={() => setBreakdownPage((p) => p + 1)} sx={{ minWidth: 0, px: 1.5 }}>Next &#8250;</Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<RateReviewIcon />}
              onClick={() => navigate(getMockExamResultPath(attemptId, 'review'))}
              sx={{ textTransform: 'none', fontWeight: 700, py: 1.2 }}
            >
              Review answers
            </Button>
            {/* Only the student portal has a mock-exam/exam/:mockExamId/report route — a parent
                following this would land on a 404. */}
            {mockExamId && getMockExamApiPrefix() !== 'parent' && (
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate(`/student/mock-exam/exam/${mockExamId}/report`)}
                sx={{ textTransform: 'none', fontWeight: 600, py: 1.2 }}
              >
                All attempts
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => navigate(getMockExamPortalHomePath())}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Back to Mock Exams
            </Button>
          </Box>
        </Box>
      )}

      {tab === 'subject' && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Subject-wise Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Detailed performance breakdown by subject.
            </Typography>
            {subjectLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Total Qs</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Correct</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Incorrect</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Unattempted</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Correct Marks</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Incorrect Marks</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Unattempted Marks</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Max Marks</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Percentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subjectRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontWeight: row.name === 'Grand Total' ? 700 : 400 }}>
                          {row.name}
                        </TableCell>
                        <TableCell>{row.paper_total_questions ?? row.total_questions}</TableCell>
                        <TableCell sx={{ color: 'success.main' }}>{row.correct}</TableCell>
                        <TableCell sx={{ color: 'error.main' }}>{row.incorrect}</TableCell>
                        <TableCell>{row.unattempted}</TableCell>
                        <TableCell>{Number(row.correct_marks).toFixed(2)}</TableCell>
                        <TableCell>{Number(row.incorrect_marks).toFixed(2)}</TableCell>
                        <TableCell>{Number(row.unattempted_marks).toFixed(2)}</TableCell>
                        <TableCell>{Number(row.max_marks).toFixed(2)}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{row.percentage}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'topic' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Topic-wise Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Detailed performance breakdown by topic.
              </Typography>
              {topicLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Total Qs</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Correct</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Incorrect</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Unattempted</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Correct Marks</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Incorrect Marks</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Unattempted Marks</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Max Marks</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Percentage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topicRows.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: row.name === 'Grand Total' ? 700 : 400 }}>
                            {row.name}
                          </TableCell>
                          <TableCell>{row.paper_total_questions ?? row.total_questions}</TableCell>
                          <TableCell sx={{ color: 'success.main' }}>{row.correct}</TableCell>
                          <TableCell sx={{ color: 'error.main' }}>{row.incorrect}</TableCell>
                          <TableCell>{row.unattempted}</TableCell>
                          <TableCell>{Number(row.correct_marks).toFixed(2)}</TableCell>
                          <TableCell>{Number(row.incorrect_marks).toFixed(2)}</TableCell>
                          <TableCell>{Number(row.unattempted_marks).toFixed(2)}</TableCell>
                          <TableCell>{Number(row.max_marks).toFixed(2)}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{row.percentage}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
          {topicChartData.length > 0 && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Topic Performance
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={topicChartData}
                    layout="vertical"
                    margin={{ left: 80, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={80} tickLine={false} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="maxMarks" name="Max Marks" fill="#8884d8" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="correctMarks" name="Correct Marks" fill="#82ca9d" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {tab === 'marks' && (
        marksBreakdownRows.length > 0 ? (
          <MarksBreakdownTable
            rows={marksBreakdownRows}
            totalMarks={summary.total_marks ?? 0}
            scoredMarks={breakdownScore}
            title="Marks Breakdown"
            fileName={`${(summary.exam_title || 'mock-exam').replace(/[^\w-]+/g, '_').slice(0, 60)}-marks`}
          />
        ) : (
          <Alert severity="info">No graded questions in this attempt.</Alert>
        )
      )}

      {tab === 'ai' && (
        <AiInsightsTab
          aiFeedbackStatus={aiFeedbackStatus}
          aiOverall={aiOverall}
          aiScore={shownScore}
          aiPercentage={summary.total_marks > 0 ? shownPercentage : aiPercentage}
          totalMarks={summary.total_marks}
          ruleScore={summary.scored_marks}
        />
      )}
    </Box>
  );
};

export default StudentMockExamReport;
